/**
 * Seo - 运行时 <head> meta 注入组件。
 *
 * 在路由 layout（LangSync）下挂载一次，根据当前 URL 自动：
 * - 设置 <title>
 * - 设置 meta[name=description]
 * - 注入 hreflang（4 语言 + x-default）+ canonical
 * - 设置 OG / Twitter card 的 title/description/url
 * - 注入 JSON-LD 结构化数据（Organization/WebSite/SoftwareApplication/FAQPage/HowTo）
 *
 * 仅对 ROUTE_META 命中的公开页生效；workbench 等登录页不注入（保留 index.html 默认）。
 * Prerender 阶段会复用 ROUTE_META 把这些 meta 写进静态 HTML，运行时组件负责路由切换时同步。
 *
 * 详见 SEO_CONTENT_PLAN.md §4.2 / §4.3。
 */

import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLang } from "../lib/lang";
import { routeMetaFromPath, buildHreflangs, SITE_URL, OG_LOCALE } from "../lib/seo";
import { upsertJsonLd, removeJsonLd } from "../lib/jsonld";
import { clearNoindex } from "../lib/meta";

const LANGS: readonly string[] = ["en", "zh", "ja", "ko", "x-default"];

/** 幂等设置或更新一个 meta 标签（按 name 或 property 匹配）。 */
function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`,
  );
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/** 幂等设置或更新 <link rel=canonical> / <link rel=alternate hreflang>。 */
function upsertLink(rel: string, href: string, hreflang?: string) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/** 清理本组件注入的 hreflang（避免语言切换时残留）。 */
function clearHreflangs() {
  for (const hl of LANGS) {
    document.head
      .querySelectorAll(`link[rel="alternate"][hreflang="${hl}"]`)
      .forEach((el) => el.remove());
  }
}

/** 移除 canonical（meta-less 应用页用：不带上一页的规范化信号） */
function removeCanonical() {
  document.head
    .querySelectorAll('link[rel="canonical"]')
    .forEach((el) => el.remove());
}

/** 全站共享的 Organization + WebSite 结构化数据。 */
function orgAndWebSiteJsonLd(lang: string) {
  return [
    {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: "Tsuvi",
      url: SITE_URL,
      logo: `${SITE_URL}/icon-512.png`,
    },
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      url: SITE_URL,
      name: "Tsuvi",
      description: "Turn any image into a print-ready 3D model.",
      publisher: { "@id": `${SITE_URL}/#organization` },
      inLanguage: lang,
    },
  ];
}

export default function Seo() {
  const location = useLocation();
  const { t, ready } = useTranslation();
  const lang = useLang();
  const meta = routeMetaFromPath(location.pathname);

  useEffect(() => {
    if (!ready) return;

    // meta-less 路由（工作台/历史等应用页）：清掉上一页残留的 noindex 与
    // hreflang/canonical（应用页不是公开文档，不应携带上一页的索引信号，P3）
    if (!meta) {
      clearNoindex();
      clearHreflangs();
      removeCanonical();
      return;
    }

    // 公开页可索引：清掉上一页（如不存在的文章 slug）可能残留的 noindex
    clearNoindex();

    // 动态文章路由（/guides/:slug 等）：title/description/OG 由文章页组件自行设置
    // （内容来自 manifest，不在 i18n 字典里）；此处只管 hreflang + canonical + 全站 JSON-LD。
    if (!meta.dynamic) {
      const title = t(meta.titleKey);
      const description = t(meta.descKey);

      // title
      document.title = title;

      // description
      upsertMeta("name", "description", description);

      // OG / Twitter
      upsertMeta("property", "og:title", title);
      upsertMeta("property", "og:description", description);
      upsertMeta("property", "og:url", `${SITE_URL}${location.pathname}`);
      upsertMeta("property", "og:type", "website");
      upsertMeta("property", "og:site_name", "Tsuvi");
      // og:locale 随页面语言覆写（模板默认 en_US；直访 SPA 未经 prerender 时由此补齐）
      upsertMeta("property", "og:locale", OG_LOCALE[lang] ?? "en_US");
      // og:image 必须是绝对 URL：社交平台（FB/LinkedIn/Slack）不解析相对路径，
      // 直接访问 SPA（未经 prerender）时 index.html 默认值不会带域名，这里统一补齐。
      upsertMeta("property", "og:image", `${SITE_URL}/og-image.png`);
      upsertMeta("name", "twitter:card", "summary_large_image");
      upsertMeta("name", "twitter:title", title);
      upsertMeta("name", "twitter:description", description);
      upsertMeta("name", "twitter:image", `${SITE_URL}/og-image.png`);
    }

    // hreflang + canonical（englishOnly 内容只发 en + x-default）
    const { hreflangs, canonical } = buildHreflangs(
      meta.path,
      lang,
      meta.englishOnly,
    );
    clearHreflangs();
    for (const h of hreflangs) {
      upsertLink("alternate", h.href, h.hreflang);
    }
    upsertLink("canonical", canonical);

    // JSON-LD：全站 Organization + WebSite（任何公开页都注入）
    // 每个块自带 @context（schema.org 允许单对象声明）
    const orgWebsite = orgAndWebSiteJsonLd(lang);
    upsertJsonLd("org", { "@context": "https://schema.org", ...orgWebsite[0] });
    upsertJsonLd("website", { "@context": "https://schema.org", ...orgWebsite[1] });

    // 落地页专属：SoftwareApplication + FAQPage + HowTo
    if (meta.path === "") {
      upsertJsonLd("app", {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "Tsuvi",
        applicationCategory: "DesignApplication",
        operatingSystem: "Web",
        url: `${SITE_URL}/${lang}`,
        description: t("seo.landing.description"),
        offers: {
          "@type": "Offer",
          price: "0",
          priceCurrency: "USD",
        },
      });

      // FAQPage：与页面可见的 FAQ 全量对齐（含 firstMonth，P3 补漏）
      const faqKeys = ["free", "firstMonth", "formats", "printReady", "commercial", "time"];
      upsertJsonLd("faq", {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqKeys.map((k) => ({
          "@type": "Question",
          name: t(`landing.faq.items.${k}.q`),
          acceptedAnswer: {
            "@type": "Answer",
            text: t(`landing.faq.items.${k}.a`),
          },
        })),
      });

      // HowTo：3 步流程
      const stepKeys = ["upload", "generate", "download"];
      upsertJsonLd("howto", {
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: t("landing.howItWorks.title"),
        description: t("landing.howItWorks.subtitle"),
        step: stepKeys.map((k, i) => ({
          "@type": "HowToStep",
          position: i + 1,
          name: t(`landing.howItWorks.steps.${k}.title`),
          text: t(`landing.howItWorks.steps.${k}.body`),
        })),
      });
    } else {
      // 非落地页：清理落地页专属 JSON-LD，避免残留
      // （/faq 由 FaqPage 自行注入 faq 块，这里不清 faq——见 FaqPage）
      removeJsonLd("app");
      removeJsonLd("faq");
      removeJsonLd("howto");
    }
  }, [meta, lang, location.pathname, t, ready]);

  return null;
}
